import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const REQUEST_PATHS = {
  createProduct: 'requests.cpCreateProductRequest',
  createAudience: 'requests.cpCreateAudienceRequest',
  createEvent: 'requests.cpCreateEventRequest',
  updateProduct: 'requests.cpUpdateProductRequest',
  updateAudience: 'requests.cpUpdateAudienceRequest',
  updateCvp: 'requests.cpUpdateCvpRequest',
  updateEvent: 'requests.cpUpdateEventRequest',
  approve: 'requests.cpApproveRequest',
  reject: 'requests.cpRejectRequest',
  recall: 'requests.cpRecallRequest',
  contentGenerationConfigApply: 'requests.contentGenerationConfigApplyRequest',
  contentGenerationConfigRollback: 'requests.contentGenerationConfigRollbackRequest',
};
const EMPTY_OBJECT = {};

const useRequest = (path) => useSelector((state) => _.get(state, path, EMPTY_OBJECT));

export const useContentConfigWrite = ({ onFailure, onSuccess } = {}) => {
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const [write, setWrite] = useState(null);
  const requests = {
    createProduct: useRequest(REQUEST_PATHS.createProduct),
    createAudience: useRequest(REQUEST_PATHS.createAudience),
    createEvent: useRequest(REQUEST_PATHS.createEvent),
    updateProduct: useRequest(REQUEST_PATHS.updateProduct),
    updateAudience: useRequest(REQUEST_PATHS.updateAudience),
    updateCvp: useRequest(REQUEST_PATHS.updateCvp),
    updateEvent: useRequest(REQUEST_PATHS.updateEvent),
    approve: useRequest(REQUEST_PATHS.approve),
    reject: useRequest(REQUEST_PATHS.reject),
    recall: useRequest(REQUEST_PATHS.recall),
    contentGenerationConfigApply: useRequest(REQUEST_PATHS.contentGenerationConfigApply),
    contentGenerationConfigRollback: useRequest(REQUEST_PATHS.contentGenerationConfigRollback),
  };

  useEffect(() => {
    if (!write) return;
    const req = requests[write.requestKey] || {};
    if (req.isPending && !write.pendingSeen) {
      setWrite((prev) => (prev ? { ...prev, pendingSeen: true } : prev));
      return;
    }
    if (!write.pendingSeen) return;
    if (req.isSuccessful) {
      setBusy(false);
      setWrite(null);
      if (onSuccess) onSuccess(write.options || {});
    } else if (req.isFailure) {
      setBusy(false);
      setWrite(null);
      if (onFailure) onFailure(write.options || {});
    }
  }, [
    write,
    onSuccess,
    onFailure,
    requests.createAudience.isPending,
    requests.createAudience.isSuccessful,
    requests.createAudience.isFailure,
    requests.createProduct.isPending,
    requests.createProduct.isSuccessful,
    requests.createProduct.isFailure,
    requests.updateProduct.isPending,
    requests.updateProduct.isSuccessful,
    requests.updateProduct.isFailure,
    requests.updateAudience.isPending,
    requests.updateAudience.isSuccessful,
    requests.updateAudience.isFailure,
    requests.updateCvp.isPending,
    requests.updateCvp.isSuccessful,
    requests.updateCvp.isFailure,
    requests.createEvent.isPending,
    requests.createEvent.isSuccessful,
    requests.createEvent.isFailure,
    requests.updateEvent.isPending,
    requests.updateEvent.isSuccessful,
    requests.updateEvent.isFailure,
    requests.approve.isPending,
    requests.approve.isSuccessful,
    requests.approve.isFailure,
    requests.reject.isPending,
    requests.reject.isSuccessful,
    requests.reject.isFailure,
    requests.recall.isPending,
    requests.recall.isSuccessful,
    requests.recall.isFailure,
    requests.contentGenerationConfigApply.isPending,
    requests.contentGenerationConfigApply.isSuccessful,
    requests.contentGenerationConfigApply.isFailure,
    requests.contentGenerationConfigRollback.isPending,
    requests.contentGenerationConfigRollback.isSuccessful,
    requests.contentGenerationConfigRollback.isFailure,
  ]);

  const runWrite = (action, requestKey, options = {}) => {
    setBusy(true);
    setWrite({ requestKey, options, pendingSeen: false });
    dispatch(action);
  };

  return { busy, runWrite };
};

export default useContentConfigWrite;
